import { memo, type Ref } from 'react';
import { cn } from '@/hen-ui/utils';
import { Tooltip } from '@/hen-ui/composites/Tooltip';
import { Icon } from '@/hen-ui/atomics/Icon';
import { DataPoint } from '@/hen-ui/composites/DataPoint';
import { SimpleDataPoint } from '@/hen-ui/composites/SimpleDataPoint';
import { Flex } from '@/hen-ui/composites/Flex';
import { Grid } from '@/hen-ui/composites/Grid';
import { expandContentVariants } from './ranking-card.variants';
import type { RankingCardProps } from './types';

type RankingCardBodyProps = Pick<
  RankingCardProps,
  'children' | 'stats' | 'details' | 'expanded'
> & {
  blurbRef?: Ref<HTMLDivElement>;
};

export const RankingCardBody = memo(function RankingCardBody({
  children,
  stats,
  details,
  expanded = false,
  blurbRef,
}: RankingCardBodyProps) {
  return (
    <Flex direction='column' gap='2xl'>
      <Flex direction='column' direction-md='row' gap='2xl'>
        {/* Blurb slot — consumer renders any React content */}
        {/* line-clamp skips lines inside flex/grid children, so CMS lists render as blocks while collapsed */}
        {children && (
          <div
            ref={blurbRef}
            data-testid='ranking-card-blurb'
            className={cn(
              'flex-1 text-b5',
              !expanded && 'line-clamp-[8] [&_ul]:!block [&_ol]:!block',
            )}
          >
            {children}
          </div>
        )}

        {/* Data grid — 2×2 bordered tiles */}
        {stats && stats.length > 0 && (
          <Flex
            direction='column'
            gap='none'
            className='w-full md:flex-1 pt-xs md:pt-0'
          >
            <Grid
              columns={2}
              gap='xs'
              role='list'
              aria-label='School statistics'
            >
              {stats.map((field) => (
                <div key={field.label} role='listitem'>
                  <DataPoint
                    size='sm'
                    style='light'
                    property={field.label}
                    value={field.value}
                  />
                </div>
              ))}
            </Grid>

            {/* Expanded detail rows */}
            <div className={expandContentVariants({ expanded })}>
              <div className='flex flex-col overflow-hidden min-h-0'>
                {details && details.length > 0 && (
                  <dl className='mt-xs md:mt-lg md:pt-xs'>
                    {details.map((field) => (
                      <SimpleDataPoint
                        key={field.label}
                        titleVariant='b5'
                        valueVariant='b5'
                        valueWeight='semibold'
                        title={
                          // gap-0.5 (2px): tightest coupling between label and info icon; no Hen token below xxs (8px)
                          field.tooltip ? (
                            <span className='flex items-center gap-0.5'>
                              {field.label}
                              <Tooltip content={field.tooltip} side='bottom'>
                                <button
                                  type='button'
                                  className='inline-flex items-center p-0 border-0 bg-transparent cursor-pointer rounded-max focus-visible:outline-none focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1'
                                  aria-label={`Info about ${field.label}`}
                                >
                                  <Icon
                                    icon='info'
                                    size='sm'
                                    className='text-secondary-700'
                                  />
                                </button>
                              </Tooltip>
                            </span>
                          ) : (
                            field.label
                          )
                        }
                        value={field.value}
                        rowDivider
                      />
                    ))}
                  </dl>
                )}
              </div>
            </div>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});
